import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { MSAL_SCOPES } from 'utils/environmentVariables';

const AuthContext = createContext<{
  isSignedIn: boolean;
  getAccessToken: () => Promise<string>;
}>({
  isSignedIn: false,
  getAccessToken: async () => '',
});

export const useAuth = () => useContext(AuthContext);

type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    if (accounts.length === 0 && inProgress === InteractionStatus.None) {
      instance
        .ssoSilent({
          scopes: [MSAL_SCOPES],
        })
        .then(() => {
          setIsSignedIn(true);
          return;
        })
        .catch((e) => {
          if (e instanceof InteractionRequiredAuthError) {
            instance.loginRedirect({
              scopes: [MSAL_SCOPES],
            });
          }
        });
    } else if (accounts.length > 0) {
      setIsSignedIn(true);
    }
  }, [instance, accounts.length, inProgress]);

  const getAccessToken = async () => {
    if (accounts.length === 0) {
      throw new Error('No authenticated account available.');
    }

    const request = {
      scopes: [MSAL_SCOPES],
      account: accounts[0],
    };

    try {
      const silentResult: AuthenticationResult = await instance.acquireTokenSilent(request);
      return silentResult.accessToken;
    } catch (error) {
      const interactionRequired = error instanceof InteractionRequiredAuthError;
      if (interactionRequired) {
        try {
          const interactiveResult: AuthenticationResult = await instance.acquireTokenPopup(request);
          return interactiveResult.accessToken;
        } catch (interactiveError) {
          throw new Error('Interaction required error: ' + interactiveError);
        }
      } else {
        throw new Error('Non-interactive error: ' + error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ isSignedIn, getAccessToken }}>{children}</AuthContext.Provider>
  );
};
